import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableHead, TableRow, TableCell, TableBody } from '@trackman/web-shared-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import Paragraph from 'components/TextComps/Paragraph';
import { useServices, useStores } from 'index';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminPagesRelativeRoutes } from 'Routes';
import { sortByString } from 'utilities/dataSorters';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { getErrorMessage } from 'utilities/ErrorHelper';
import { useGetMasters } from 'queryHooks/masters';

export const MastersList = () => {
  useSetPageTitle({ title: 'Masters List' });
  const queryClient = useQueryClient();
  const notificationsStore = useStores().notificationStore;
  const mastersEditorService = useServices().mastersEditorService;

  const { data: mastersList, isPending } = useGetMasters();

  const [isModalOn, setIsModalOn] = useState(false);
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');
  const [searchValue, setSearchValue] = useState('');
  const [masterToDelete, setMasterToDelete] = useState<string | null>(null);

  const mastersListToDisplay = mastersList
    ? sortByString(
        mastersList.filter((master) => master.fullName.toLowerCase().includes(searchValue.toLowerCase())),
        'fullName',
        sortOrder
      )
    : [];

  const deleteMaster = useMutation({
    mutationFn: () => mastersEditorService.deleteMaster(masterToDelete ?? ''),
    onSuccess: async () => await Promise.all([queryClient.invalidateQueries({ queryKey: ['masters'] })]),
    onError: (ex) => notificationsStore.addToast('error', getErrorMessage(ex), 'error'),
    onSettled: () => {
      setMasterToDelete(null);
      setIsModalOn(false);
    },
  });

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Masters List</h1>
      </div>
      <div className='admin-content-container'>
        <div className='admin-list-header'>
          <HeaderSearchbar searchValue={searchValue} onChange={setSearchValue} />
          <HeaderCreateButton buttonTitle='New master' route={AdminPagesRelativeRoutes.MastersEditor} />
        </div>

        <div className='tmu-table-wrapper'>
          <Table className='tmu-table admin-table'>
            <TableHead>
              <TableRow>
                <TableCell>
                  Name
                  <SortOrderButton isActive={true} sortOrder={sortOrder} onSortChange={(sortOrder) => setSortOrder(sortOrder)} />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mastersListToDisplay.length && !isPending ? (
                mastersListToDisplay.map((row) => {
                  return (
                    <TableRow hover tabIndex={-1} key={row.id}>
                      <TableCell>{row.fullName}</TableCell>
                      <TableCell>
                        <div className='action-buttons'>
                          <Link to={`/Admin/${AdminPagesRelativeRoutes.MastersEditor}/${row.id}`} className='row-button'>
                            <PencilIcon />
                          </Link>
                          <button
                            className={`row-button ${isPending ? 'disabled' : ''}`}
                            onClick={() => {
                              setMasterToDelete(row.id);
                              setIsModalOn(true);
                            }}
                            disabled={isPending}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Paragraph>
                      <>No masters found</>
                    </Paragraph>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <DeleteListItemModal
          isModalOn={isModalOn}
          setIsModalOn={setIsModalOn}
          deleteAction={() => deleteMaster.mutate()}
          itemName='master'
        />
      </div>
    </>
  );
};
